import { SalesOrderUpdatedEvent } from '@rematter/frontend-lib-api-client/grpc/trade/salesOrder_events_pb';
import { Col, DiffViewer, FieldDisplay, IDField } from '@rematter/ui-web';

import { salesOrderTypeLabels, salesOrderStatusLabels } from '../enumLabels';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type SalesOrderUpdatedDetailProps = {
  event: SalesOrderUpdatedEvent;
};

export const SalesOrderUpdatedDetail = ({
  event,
}: SalesOrderUpdatedDetailProps) => {
  const t = useTranslations();
  const previous = event.previousSalesOrder;
  const updated = event.updatedSalesOrder;

  if (!previous || !updated) {
    return (
      <Col className="@SalesOrderUpdatedDetail items-center justify-center p-4 text-neutral/40">
        <span>{t.eventLogs.noDataAvailable}</span>
      </Col>
    );
  }

  const diffs = [
    {
      field: 'documentNumber',
      label: t.eventLogs.salesOrder.fields.documentNumber,
      previous: previous.documentNumber,
      current: updated.documentNumber,
    },
    {
      field: 'type',
      label: t.eventLogs.salesOrder.fields.type,
      previous: salesOrderTypeLabels[previous.type],
      current: salesOrderTypeLabels[updated.type],
    },
    {
      field: 'status',
      label: t.eventLogs.salesOrder.fields.status,
      previous: salesOrderStatusLabels[previous.status],
      current: salesOrderStatusLabels[updated.status],
    },
    {
      field: 'facilityId',
      label: t.eventLogs.salesOrder.fields.facilityId,
      previous: previous.facilityId?.value,
      current: updated.facilityId?.value,
    },
    {
      field: 'accountId',
      label: t.eventLogs.salesOrder.fields.accountId,
      previous: previous.accountId?.value,
      current: updated.accountId?.value,
    },
    {
      field: 'contactId',
      label: t.eventLogs.salesOrder.fields.contactId,
      previous: previous.contactId?.value,
      current: updated.contactId?.value,
    },
    {
      field: 'counterpartyReferenceNumber',
      label: t.eventLogs.salesOrder.fields.counterpartyReference,
      previous: previous.counterpartyReferenceNumber,
      current: updated.counterpartyReferenceNumber,
    },
    {
      field: 'paymentCurrency',
      label: t.eventLogs.salesOrder.fields.paymentCurrency,
      previous: previous.paymentCurrency,
      current: updated.paymentCurrency,
    },
    {
      field: 'recyclerExternalNotes',
      label: t.eventLogs.salesOrder.fields.recyclerExternalNotes,
      previous: previous.recyclerExternalNotes,
      current: updated.recyclerExternalNotes,
    },
    {
      field: 'recyclerInternalNotes',
      label: t.eventLogs.salesOrder.fields.recyclerInternalNotes,
      previous: previous.recyclerInternalNotes,
      current: updated.recyclerInternalNotes,
    },
    {
      field: 'customerExternalNotes',
      label: t.eventLogs.salesOrder.fields.customerExternalNotes,
      previous: previous.customerExternalNotes,
      current: updated.customerExternalNotes,
    },
    {
      field: 'customerInternalNotes',
      label: t.eventLogs.salesOrder.fields.customerInternalNotes,
      previous: previous.customerInternalNotes,
      current: updated.customerInternalNotes,
    },
    {
      field: 'completionNotes',
      label: t.eventLogs.salesOrder.fields.completionNotes,
      previous: previous.completionNotes,
      current: updated.completionNotes,
    },
  ];

  return (
    <Col className="@SalesOrderUpdatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          {t.eventLogs.salesOrder.sectionTitle}
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={updated.id?.value} />
          <FieldDisplay
            label={t.eventLogs.salesOrder.documentNumber}
            value={updated.documentNumber}
          />
        </div>
      </Col>

      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap1>
        <span className="mb-2 text-xs font-medium text-neutral/60">
          {t.eventLogs.salesOrder.changes}
        </span>
        <DiffViewer
          diffs={diffs}
          previousLabel={t.eventLogs.salesOrder.previous}
          currentLabel={t.eventLogs.salesOrder.updated}
        />
      </Col>
    </Col>
  );
};
